import { initializeIcons, ThemeProvider } from '@fluentui/react';
import * as microsoftTeams from '@microsoft/teams-js';
import * as React from 'react';
import { Suspense } from 'react';
import { Route, BrowserRouter as Router, Switch } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import { getQueryFromDeepLink, initializeGetHelpIcons } from '../../../../web/utils/getHelputils';
import { addOrUpdateAwaMetaTag, capturePageView, logCustomError } from '../../../../web/utils/oneDsUtil';
import { getSocMarket, getSynchronousLocale, getUserContext } from '../../../../web/utils/statelessUtil';
import { AuthInfo, Modality } from '../../../models/apiModels';
import {
  AwaMetaTagType,
  ContactUsAssetID,
  ContactUsEntryPoint,
  ContactUsExperienceVersion,
  ContactUsPageType,
  Environment,
  NavBarSettings,
  SourceApps,
  URLs,
  EmeraldConfigFallbackBody,
  FallbackTheme,
  OutlookDotComAlchemyTheme,
  OutlookDotComHostOrigin,
  isCMCRequestCreationDeeplinkKey,
} from '../../../models/constants/constants';
import { IGlobalState, ISessionData, IUITabViewInfo, UITabView, UITabViewInfo } from '../../../models/reduxState';
import {
  Browser,
  BrowserOffset,
  ClientAuthProvider,
  PlatformVersion,
  Theme,
  getDisplayNamespace,
  getThemeFromUrl,
  isMac,
  mapTheme,
  parseTeamsTheme,
  uwpNamespaces,
} from '../../../utilities';
import { BackgroundAuthContainer } from '../../contactUs/containerComponents/backgroundAuthContainer';
import { ThirdPartyCookiesDisabledContainer } from '../../contactUs/containerComponents/ThirdPartyCookiesDisabledContainer';
import { EmeraldConfigRequestModel, EmeraldConfigResponseModel } from '../../contactUs/models/configModels';
import { FreelanceRequest } from '../../contactUs/models/requestModels';
import { httpUtils } from '../../../../web/common/apis/httpUtils';
import { IContactUsCurrentTheme } from '../../contactUs/styles/theme.model';
import { appContext } from '../../../../web/models/getHelp/appContext';

const ContactUsAppContainer = React.lazy(async () => {
  const module = await import('../../contactUs/containerComponents/ContactUsAppContainer');
  return { default: module.ContactUsAppContainer };
});

const ConsentPopup = React.lazy(() => import('../../contactUs/auth/ConsentPopup'));
const ClosePopup = React.lazy(() => import('../../contactUs/auth/ClosePopup'));

export interface AppInitializerProps {
  queryParameterDictionary: { [key: string]: string };
  theme: IContactUsCurrentTheme;
  environment: Environment;
  sourceApp: string;
  initializationComplete: boolean;
  socDataGrabComplete: boolean;
  globalState: IGlobalState;
  sessionData: ISessionData;
  hasThirdPartyCookiesEnabled: boolean;
  isGetHelp: boolean;
  isWindowsCopilotRequestCreationDeeplink: boolean;
  isCMCRequestCreationDeeplink: boolean;
  contactSearchedQuery: string;
  dispatchGetEmeraldConfig: (body: EmeraldConfigRequestModel, environment: Environment) => void;
  dispatchEmeraldSupportConfig: (request: EmeraldConfigRequestModel, environment: Environment, appVer: string) => void;
  dispatchGetEmeraldConfigSuccess: (initData: EmeraldConfigResponseModel, environment: Environment, sourceApp: SourceApps) => void;
  dispatchUserInfoFetch: () => void;
  dispatchGetInfoForClientTokenRetrieval: () => void;
  setTheme: (theme: string) => void;
  setAutoSuggestEndpoint: (endpointUrl: string) => void;
  setDisplayNamespace: (displayNamespace: string) => void;
  setInitializationComplete: () => void;
  setNavBarSettings: (navbarsettings: NavBarSettings) => void;
  dispatchTeamsAsyncCallback: () => void;
  updateDraftRequest: (properties: Partial<FreelanceRequest>) => void;
  setContactSearchedQueryText: (query: string, isAutoSuggestQuery: boolean) => void;
  initializeModalityStateOnGetHelp: (modalities: Modality[]) => void;
  setAuthInfoForTeams: (authInfo: AuthInfo) => void;
  updateUITabViewInfo: (uiTabViewInfo: IUITabViewInfo) => void;
  setMarket: (market: string) => void;
  dispatchFetchAvailableModalitiesV2: () => void;
  setWindowsUpdateAvailableForSA: (isWindowsUpdateAvailableForSA: boolean) => void
  setEndOfLifeMessageForSA: (isEndOfLifeReached: boolean) => void
  dispatchAlchemyFetch: (searchText: string, actualQuery?: string, isSpecialQuery?: boolean, byPassPreProcess?: boolean) => any;
}
export class AppInitializer extends React.Component<AppInitializerProps, null> {
  public constructor(props: any) {
    super(props);
    // Do this first so any subsequent calls will have the ems session id to add to any
    // HTTP requests.
    const emsSessionId: string = this.getEmsSessionIdFromDocument();
    httpUtils.setEmsSessionId(emsSessionId);
    addOrUpdateAwaMetaTag(AwaMetaTagType.EmsSessionId, emsSessionId);

    // Set UserAgent data for windows os and browser.
    try {
      this.initializeUserAgentWindowsOS();
    } catch (e) {
      logCustomError(`Failed to write ClientContext off UserAgent data. ClientContext: ${window.ClientContext}, UserAgent: ${(window as any)?.userAgent}`);
    }

    this.getSocData();
    // For ThirdParty we don't need to call userInfo for mvp 0
    if (
      !(
        this.props.globalState.namespace &&
        uwpNamespaces.indexOf(this.props.globalState.namespace) !== -1
      ) &&
      this.props.environment !== Environment.ThirdParty &&
      // Todo: Brook - Logic for gethelp to get userInfo needs to be updated after we get the token to silently sign in the user
      this.props.environment !== Environment.GetHelp &&
      // sourceapp=smbhub1 is UnAuth entry point that does not need home/userinfo call.
      this.props.sourceApp !== SourceApps.SmbHub1
    ) {
      // For UWP, do not fetch user info until background auth finised.
      this.props.dispatchUserInfoFetch();
    }
  }

  public componentDidMount = () => {
    initializeIcons(URLs.IconBaseUrl, { disableWarnings: true });
    if (this.props.isGetHelp) {
      initializeGetHelpIcons();
    }
  };

  public initializeGetHelpApp = () => {
    const request: EmeraldConfigRequestModel = {
      Host: null,
      Platform: null,
      Market: getSynchronousLocale() || 'en-us',
      Version: this.props.globalState.version,
      Namespace: this.props.globalState.namespace,
      LCID: null,
    };
    this.props.dispatchGetEmeraldConfig(request, this.props.environment);
    // for Windows Copilot deeplink, we want to skip self-help stage but still show theme routing dropdown
    if (this.props.isWindowsCopilotRequestCreationDeeplink){
      const [searchText] = getQueryFromDeepLink(appContext.deeplinkPath);
      this.deepLinkToThemeRoutingDropdown(request.Market, searchText, false);
    }
    // Todo: Brook add logic for MSA/ AADv1 token update
    // Todo: Brook setting isContactUs to true, see if we should push different history for gethelp
  };

  // initialize SmbHub experience which is unauth and only has ticket creation
  public initializeSmbHub = (market: string, query: string) => {
    const smbHubDraftRequest = new FreelanceRequest();
    // MSaaS requires user description
    smbHubDraftRequest.Title = query;
    // SmbHub needs a unique identifier since there is no self-help query, so userdescription will be set as a guid
    smbHubDraftRequest.UserDescription = uuidv4();
    this.props.updateDraftRequest(smbHubDraftRequest);
    this.initializeRequestCreationOnly(market, query);
    // SmbHub is unauth so dispatch modality call to bypass sign in stage
    this.props.dispatchFetchAvailableModalitiesV2();
  };

  // initialize AMC experience which only has ticket creation
  public initializeAMC = (market: string, query: string) => {
    this.props.updateDraftRequest(new FreelanceRequest());
    this.initializeRequestCreationOnly(market, query);
  };

  // Initialize ThirdParty app
  public initializeThirdPartyApp = () => {
    const request: EmeraldConfigRequestModel = {
      Host: null,
      Platform: null,
      Market: getSocMarket(),
      Version: this.props.globalState.version,
      Namespace: this.props.globalState.namespace,
      LCID: null,
    };
    this.props.dispatchGetEmeraldConfig(request, this.props.environment);
  };

  // Initialize bridge for client communications and callbacks for Mac.
  public initializeMacBridge = (macWindow: any): void => {
    macWindow.WVJBCallbacks = [];
    const WVJBIframe = document.createElement('iframe');
    WVJBIframe.style.display = 'none';
    WVJBIframe.src = 'https://__bridge_loaded__';
    document.documentElement.appendChild(WVJBIframe);
    setTimeout(() => document.documentElement.removeChild(WVJBIframe), 0);
  };

  // function that calls SOC for initialization data with the request data being constructed with Agave provided context data
  public initializeAgavePane = () => {
    // Agave panes do not have the request data needed to grab init data on the first call to SOC. The request data is available when Office onReady is called.
    // When the request data is available we make a second call to SOC to get all init data
    Office.onReady((info: any) => {
      if (Office.context.host == null) {
        this.initializeFallback();
      } else {
        console.log(`Office.js is now ready in ${info.host} on ${info.platform}`);
        const theme = Office.context.officeTheme == null ? 'white' : mapTheme(Office.context.officeTheme.bodyBackgroundColor);
        this.props.setTheme(theme);

        const request: EmeraldConfigRequestModel = {
          Host: Office.context.host.toString(),
          Platform: Office.context.platform.toString(),
          Market: Office.context.displayLanguage,
          Version: '16',
          Namespace: null,
          LCID: null,
        };
        this.props.dispatchGetEmeraldConfig(request, this.props.environment);
      }
    }).catch(() => {
      this.initializeFallback();
    });
  };

  // Function that calls SOC for initialization data with the request data being constructed with query param data provided in non Agave panes using the react experience
  public initializeNonAgavePane = () => {
    // Non Agave instances populate the ResponsiveNavReact init data object on the first call to SOC and store the object within home.cshtml.
    // Therefore we do not need to call SOC to get the init data as it as already stored in the DOM, instead we grab the init data and store it in redux.
    // tslint:disable-next-line:no-string-literal
    const objectFromRazor: any = window['ResponsiveNavReact'];
    const namespace = objectFromRazor.nameSpace.toLowerCase();

    const initResponse: EmeraldConfigResponseModel = {
      AppVer: objectFromRazor.appVer,
      DefaultArticleGuid: objectFromRazor.defaultArticle,
      Market: objectFromRazor.market,
      Namespace: namespace,
      NavigationBarSettings: NavBarSettings[objectFromRazor.navBarSettings as string],
      Version: objectFromRazor.version,
      SupportSettingsConfig: objectFromRazor.SupportSettingsConfig,
    };
    const request: EmeraldConfigRequestModel = {
      Host: null,
      Platform: null,
      Market: objectFromRazor.market,
      Version: null,
      Namespace: null,
      LCID: null,
    };
    this.props.dispatchGetEmeraldConfigSuccess(initResponse, this.props.environment, this.props.globalState.sourceApp);
    this.props.dispatchEmeraldSupportConfig(request, this.props.environment, initResponse.AppVer);

    const theme = objectFromRazor && objectFromRazor.theme !== null ? objectFromRazor.theme.toLowerCase() : Theme[Theme.colorful];
    this.props.setTheme(theme);

    this.props.dispatchGetInfoForClientTokenRetrieval();

    this.initializeSharedState(namespace, theme);

    if (isMac(namespace)) {
      this.initializeMacBridge((window as any));
    }

    // For outlook.com, have to update ticket attributes from query params since there is no self help
    if (this.props.environment === Environment.OutlookDotCom) {
      this.initializeOutlookDotCom();
    }
  };

  // Function that sets attributes for OutlookDotCom ticket and other configs
  public initializeOutlookDotCom = () => {
    // Create draftrequest for GetAvailableModality/AddRequest calls using attributes from URL query string
    const outlookDraftRequest = new FreelanceRequest();
    const queryparams = this.props.queryParameterDictionary;

    const extensionAttributes = outlookDraftRequest.ExtensionAttributes;
    extensionAttributes.Theme = OutlookDotComAlchemyTheme;
    extensionAttributes.BucketId = queryparams.alchemybucketid;
    outlookDraftRequest.ExtensionAttributes = extensionAttributes;
    outlookDraftRequest.UserDescription = queryparams.query;

    this.props.updateDraftRequest(outlookDraftRequest);

    // Make request to Outlook.com host for additional metadata and store it in the draftrequest.
    const setAdditionalMetadata = (event?: MessageEvent) => {
      if (!!event) {
        event.preventDefault();
        event.stopPropagation();
        if (event.origin === OutlookDotComHostOrigin) {
          try {
            const metaDataObject = JSON.parse(event.data);
            const metaDataArray = Object.keys(metaDataObject).map(key => ({ Key: key.toString(), Value: metaDataObject[key].toString() }));
            this.props.updateDraftRequest({ MetaDatas: metaDataArray });
          } catch {
            console.log('error parsing outlook.com additional metadata.');
          }
        }
      }
    };
    window.addEventListener('message', setAdditionalMetadata);
    window.top.postMessage('RequestMetadata', OutlookDotComHostOrigin);

    // Set contactSearchedQuery so the first dispatchUserInfoFetch call will dispatch getModalitiesV2, there is no second dispatchUserInfoFetch call for Outlook since no self-help
    this.props.setContactSearchedQueryText(queryparams.query, false);

    // Set isRenderingModalitySection to true and isCollapsedModalities to false in order to load the modalityOptions container
    this.props.initializeModalityStateOnGetHelp(null);
  };

  // function that calls SOC for initialization data needed to render the react contact support experience in SOC
  public initializeSOC = () => {
    const market = getSocMarket();
    const request: EmeraldConfigRequestModel = {
      Host: null,
      Platform: null,
      Market: market,
      Version: this.props.globalState.version,
      Namespace: this.props.globalState.namespace,
      LCID: null,
    };
    this.props.dispatchGetEmeraldConfig(request, this.props.environment);

    if (
      this.props.sourceApp === SourceApps.Teams ||
      this.props.sourceApp === SourceApps.Teams2 ||
      this.props.sourceApp === SourceApps.TeamsAdmin ||
      this.props.sourceApp === SourceApps.TeamsAdmin2
    ) {
      const userContextObj = getUserContext();

      microsoftTeams.initialize();
      // Store theme from QS in redux state
      const theme =
        userContextObj && userContextObj.TeamsTheme
          ? parseTeamsTheme(userContextObj.TeamsTheme)
          : Theme[Theme.colorful];
      this.props.setTheme(theme);
      // Adding CSS from contact.css
      this.addToBodyElement('teams', theme);
      // send the action to asynchronously load the teams SDK
      this.props.dispatchTeamsAsyncCallback();
      microsoftTeams.appInitialization.notifySuccess();
    } else if (this.props.sourceApp === SourceApps.SurfaceApp || this.props.sourceApp === SourceApps.SurfaceApp2) {
      const theme = getThemeFromUrl();
      this.props.setTheme(theme);
      // Adding CSS from contact.css
      this.addToBodyElement('surfaceapp', theme);
    } else if (this.props.sourceApp === SourceApps.AMC) {
      this.initializeAMC(market, 'amc');
    } else if (this.props.sourceApp === SourceApps.SmbHub1) {
      this.initializeSmbHub(market, 'SMBHub');
    } else if (this.props.sourceApp === SourceApps.OneDriveWeb) {
      const theme = getThemeFromUrl();
      this.props.setTheme(theme);
    } else if (this.props.isCMCRequestCreationDeeplink){
      const query = this.props.queryParameterDictionary?.linkquery ?? '';
      this.deepLinkToThemeRoutingDropdown(market, query.substring(isCMCRequestCreationDeeplinkKey.length), false);
      this.props.updateDraftRequest({ MetaDatas: [{ Key: "CMCReferrerDeepLink", Value: window.location.href }] });
    }

    if (this.props.sourceApp === SourceApps.Teams || this.props.sourceApp === SourceApps.Teams2) {
      const authTokenRequest = {
        successCallback: (result: any) => {
          const authInfo: AuthInfo = {
            provider: ClientAuthProvider.AAD,
            token: result,
          };
          this.props.setAuthInfoForTeams(authInfo);
          this.props.dispatchUserInfoFetch();
        },
        failureCallback: (error: any) => {
          const errorMessage = `Error getting token: ${error}`;
          logCustomError(errorMessage);
          console.log(errorMessage);
        },
      };
      microsoftTeams.authentication.getAuthToken(authTokenRequest);
    }

    // Surafce sends token with iframe.contentWindow.postMessage
    // todo: change the event handling for the End of life support devices.
    if (this.props.sourceApp === SourceApps.SurfaceApp || this.props.sourceApp === SourceApps.SurfaceApp2) {
      window.chrome.webview.addEventListener(
        'message',
        (event: { data: any; }) => {
          if (!!event && !!event.data) {
            if (event.data.substring(0, 4).toLocaleLowerCase() === 'true') {
              this.props.setWindowsUpdateAvailableForSA(true);
            }
            else if (event.data.substring(0, 5).toLocaleLowerCase() === 'false') {
              this.props.setWindowsUpdateAvailableForSA(false);
            }
            else if (event.data.substring(0, 14).toLocaleLowerCase() === 'endoflife_true') {
              this.props.setEndOfLifeMessageForSA(true);
            }
            else if (event.data.substring(0, 15).toLocaleLowerCase() === 'endoflife_false') {
              this.props.setEndOfLifeMessageForSA(false);
            }
            else {
              const authInfo: AuthInfo = {
                // A valid JWT token always starts with 'ey' for AAD and 'Ew' for MSA accounts
                // For reference https://stackoverflow.com/questions/49517324/why-header-and-payload-in-the-jwt-token-always-starts-with-eyj
                provider: event.data.substring(0, 2).toLocaleLowerCase() === 'ey' ? ClientAuthProvider.AAD : ClientAuthProvider.MSA,
                token: event.data,
              };
              this.props.setAuthInfoForTeams(authInfo);
              this.props.dispatchUserInfoFetch();
            }
          }
        },
        { once: false }
      );
      window.chrome.webview.postMessage('EventListnerRegistered');
    }

    // Teams Admin Center App sends token with iframe.contentWindow.postMessage
    if (
      this.props.sourceApp === SourceApps.TeamsAdmin ||
      this.props.sourceApp === SourceApps.TeamsAdmin2 ||
      this.props.sourceApp === SourceApps.OfficeDotCom ||
      this.props.sourceApp === SourceApps.OutlookAdmin ||
      this.props.sourceApp === SourceApps.LightHouse ||
      this.props.sourceApp === SourceApps.OneDriveWeb
    ) {
      window.addEventListener(
        'message',
        event => {
          // verify the event before setting authInfo
          // need to check eevnt.origin once outlook admin app is confirmed
          // A valid jwt always starts with 'ey', might be changed to jwt.verify if we introduce jsonwebtoken package in the future
          if (!!event && !!event.data && typeof event.data === 'string') {
            let provider: ClientAuthProvider;

            if (event.data.substring(0, 2).toLocaleLowerCase() === 'ey') {
              provider = ClientAuthProvider.AAD;
            } else if (this.props.sourceApp === SourceApps.OneDriveWeb) {
              provider = ClientAuthProvider.MSA;
            }

            if (provider) {
              const authInfo: AuthInfo = {
                provider,
                token: event.data,
              };
              this.props.setAuthInfoForTeams(authInfo);
              this.props.dispatchUserInfoFetch();
            }
          }
        },
        { once: true }
      );
    }
  };

  //
  // Contact Support Initialization Functions
  //

  // function to initialize the help state (called after initialization date is provided to the app from SOC)
  public initializeContactSupport = () => {
    addOrUpdateAwaMetaTag(AwaMetaTagType.PageType, ContactUsPageType);
    addOrUpdateAwaMetaTag(AwaMetaTagType.AssetID, ContactUsAssetID);
    addOrUpdateAwaMetaTag(AwaMetaTagType.InAppExternalNavigationType, ContactUsEntryPoint);
    addOrUpdateAwaMetaTag(AwaMetaTagType.ExperienceVersion, ContactUsExperienceVersion);
    this.props.setInitializationComplete();
    addOrUpdateAwaMetaTag(AwaMetaTagType.Product, this.props.globalState.appVer);
    addOrUpdateAwaMetaTag(AwaMetaTagType.ReferApp, this.props.globalState.appVer);

    if (!!this.props.globalState.sourceApp) {
      addOrUpdateAwaMetaTag(AwaMetaTagType.SourceApp, this.props.globalState.sourceApp);
    }
    if(this.props.environment === Environment.agavePane && !!this.props.globalState.appVer) {
      addOrUpdateAwaMetaTag(AwaMetaTagType.SourceApp, this.props.globalState.appVer);
    }
    const awaPageViewData = {};
    capturePageView(awaPageViewData);

    return;
  };

  //
  // Set UserAgent Windows OS and browser data in window.
  //
  public initializeUserAgentWindowsOS = async (): Promise<void> => {
    // Browser Data
    const userAgent = navigator.userAgent;
    let os; let osVersion;

    // List of possible UserAgent OS strings for Windows.
    // https://docs.microsoft.com/en-us/microsoft-edge/web-platform/how-to-detect-win11
    // https://donsnotes.com/tech/applications/user-agent-strings.html
    // https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/User-Agent
    const osStringsAndRegEx = [
      { s: 'Windows 10', r: /(Windows 10.0|Windows NT 10.0)/ },
      { s: 'Windows 8.1', r: /(Windows 8.1|Windows NT 6.3)/ },
      { s: 'Windows 8', r: /(Windows 8|Windows NT 6.2)/ },
      { s: 'Windows 7', r: /(Windows 7|Windows NT 6.1)/ },
      { s: 'Windows Vista', r: /Windows NT 6.0/ },
      { s: 'Windows Server 2003', r: /Windows NT 5.2/ },
      { s: 'Windows XP', r: /(Windows NT 5.1|Windows XP)/ },
      { s: 'Windows 2000', r: /(Windows NT 5.0|Windows 2000)/ },
      { s: 'Windows ME', r: /(Win 9x 4.90|Windows ME)/ },
      { s: 'Windows 98', r: /(Windows 98|Win98)/ },
      { s: 'Windows 95', r: /(Windows 95|Win95|Windows_95)/ },
      { s: 'Windows NT 4.0', r: /(Windows NT 4.0|WinNT4.0|WinNT|Windows NT)/ },
      { s: 'Windows CE', r: /Windows CE/ },
      { s: 'Windows 3.11', r: /Win16/ },
    ];

    const getBrowserInfo = (userAgent: string): { browser: string, version: string } => {
      // This sequence cannot be changed in order to identify Browsers. Since userAgent is unreliable, this method could be deprecated in
      // the future: https://developer.mozilla.org/en-US/docs/Web/HTTP/Browser_detection_using_the_user_agent
      return (userAgent.indexOf(BrowserOffset.Edg) !== -1) ? {
        browser: Browser.Edge, version: userAgent.split(BrowserOffset.Edg)[1].split(' ')[0],
      }
        : (userAgent.indexOf(BrowserOffset.MSIE) !== -1) ? {
          browser: Browser.InternetExplorer, version: userAgent.split(BrowserOffset.MSIE)[1].split(' ')[0],
        }
          : (userAgent.indexOf(BrowserOffset.MSIE11) !== -1) ? {
            browser: Browser.InternetExplorer, version: userAgent.split(BrowserOffset.MSIE11)[1].split(' ')[0],
          }
            : (userAgent.indexOf(BrowserOffset.Chrome) !== -1) ? {
              browser: Browser.Chrome, version: userAgent.split(BrowserOffset.Chrome)[1].split(' ')[0],
            }
              : (userAgent.indexOf(BrowserOffset.Firefox) !== -1) ? {
                browser: Browser.Firefox, version: userAgent.split(BrowserOffset.Firefox)[1].split(' ')[0],
              }
                : { browser: Browser.Unknown, version: '' };
    };

    for (const id in osStringsAndRegEx) {
      if (Object.prototype.hasOwnProperty.call(osStringsAndRegEx, id)) {
        const cs = osStringsAndRegEx[id];
        if (cs.r.test(userAgent)) {
          if (cs.s === PlatformVersion.Windows10) {
            let userAgentHighEntropyValues;
            if (navigator.userAgentData && navigator.userAgentData.getHighEntropyValues) {
              userAgentHighEntropyValues = navigator.userAgentData.getHighEntropyValues(['platform', 'platformVersion']);
            }

            if (userAgentHighEntropyValues) {
              await userAgentHighEntropyValues.then((highEntropyValues: HighEntropyValues) => {
                if (highEntropyValues.platform === 'Windows') {
                  // navigator.userAgentData currently does not return Windows 11 but we can tell if a client is Windows 11 by platformVersion in the HighEntropy obj.
                  const version = parseInt(highEntropyValues.platformVersion.split('.')[0], 10);
                  os = version >= 13 ? PlatformVersion.Windows11 : version > 0 ? PlatformVersion.Windows10 : PlatformVersion.Unknown;
                } else {
                  os = PlatformVersion.Unknown;
                }
              }).catch(err => err);
            }
          } else {
            os = cs.s === undefined ? PlatformVersion.Unknown : cs.s;
          }
          break;
        }
      }
    }

    if (/Windows/.test(os)) {
      osVersion = /Windows (.*)/.exec(os)[1];
      os = 'Windows.Desktop';
    } else {
      os = PlatformVersion.Unknown;
      osVersion = '';
    }

    const browserInfo = getBrowserInfo(userAgent);

    const clientContext = {
      Browser: browserInfo ? browserInfo.browser : undefined,
      BrowserVersion: browserInfo ? browserInfo.version : undefined,
      OS: os,
      MajorOSVersion: osVersion,
    };
    // window.ClientContext is added to window so we don't have to parse navigator.UserAgentData and navigator.ClientInformation each time for API calls
    // Add client context from the URL
    window.ClientContext = Object.assign({}, clientContext, getUserContext());
    // Freeze the client information gathered to prevent users from reassigning values.
    Object.freeze(window.ClientContext);

    // Add os data to an awa tag to collect for telemetery
    addOrUpdateAwaMetaTag(AwaMetaTagType.OperatingSystem, `${os} ${osVersion}`);
    return;
  };

  public render() {
    if (this.props.initializationComplete) {
      return (
        <ThemeProvider
          theme={this.props.theme}
          style={this.props.environment === Environment.GetHelp ? { height: '100%' } : undefined}
        >
          {this.body()}
        </ThemeProvider>
      );
    }

    if (this.props.socDataGrabComplete) {
      if (this.props.environment !== Environment.nonAgavePane && this.props.environment !== Environment.OutlookDotCom && this.props.environment !== Environment.ThirdParty && this.props.environment !== Environment.GetHelp) {
        // non agave pane has already initialized shared state in its init function
        this.initializeSharedState();
      }
      this.initializeContactSupport();
    }
    return null;
  }

  private getEmsSessionIdFromDocument = () => {
    // querySelector does not support the 'i' option in IE.  Since Axios changes the headers
    // to lowercase, I cannot simply search for emsSessionIdHeaderKey, which is lowercase, in
    // the meta tags.
    const emsSessionIdElement = document.querySelector(`meta[name="X-Ems-SessionId"]`);
    let emsSessionId: string = '';
    if(!!emsSessionIdElement) {
      emsSessionId = emsSessionIdElement.attributes['content'].value;
    }
    return emsSessionId;
  };

  private getSocData = () => {
    switch (this.props.environment) {
      case Environment.agavePane:
        this.initializeAgavePane();
        break;
      case Environment.OutlookDotCom:
      case Environment.nonAgavePane:
        this.initializeNonAgavePane();
        break;
      case Environment.SOC:
        this.initializeSOC();
        break;
      case Environment.ThirdParty:
        this.initializeThirdPartyApp();
        break;
      case Environment.GetHelp:
        this.initializeGetHelpApp();
        break;
      default:
        console.log('running in unsupported environment');
        break;
    }
  };

  // initializes service request creation without self-help
  private initializeRequestCreationOnly = (market: string, query: string, updateQuery = true) => {
    // manually set market in case GetAvailableModalities call gets triggered before GetEmeraldConfig finishes
    this.props.setMarket(market);
    // Set contactSearchedQuery so the first dispatchUserInfoFetch call will dispatch getModalitiesV2, there is no second dispatchUserInfoFetch call since AMC doesn't use self-help
    if (updateQuery){
      this.props.setContactSearchedQueryText(query, false);
    }
    // Set isRenderingModalitySection to true and isCollapsedModalities to false in order to load the modalityOptions container
    this.props.initializeModalityStateOnGetHelp(null);
  };

  private deepLinkToThemeRoutingDropdown = (market: string, query: string, updateQuery: boolean) => {
    this.initializeRequestCreationOnly(market, query, updateQuery)
    // dispatch Alchemy call to get theme L0 and render theme routing after
    this.props.dispatchAlchemyFetch(query, null, false, false)
    const uiTabViewInfo = new UITabViewInfo();
    uiTabViewInfo.uITabView = UITabView.requestCreation;
    this.props.updateUITabViewInfo(uiTabViewInfo);
  }

  private initializeFallback = () => {
    this.props.dispatchGetEmeraldConfig(EmeraldConfigFallbackBody, this.props.environment);
    this.props.setTheme(FallbackTheme);
  };

  //
  // Shared initialization functions for in app experiences
  //
  private initializeSharedState = (namespace?: string, theme?: string) => {
    if (this.props.globalState.environment !== Environment.SOC) {
      const appNamespace = namespace ? namespace : this.props.globalState.namespace;
      this.addToBodyElement(appNamespace, theme ? theme : this.props.globalState.theme);
      this.props.setDisplayNamespace(getDisplayNamespace(appNamespace));
    }
  };

  // Inject user auth data into page metatags and 1DS user.localId property.
  private injectSessionDataTelemetry(sessionData: ISessionData) {
    if (sessionData) {
      try {
        const { userObjectId, authType, tenantId, sessionId } = sessionData;
        const analytics = (window as any).analytics;
        if (analytics && userObjectId) {
          analytics.getPropertyManager().getPropertiesContext().user.setLocalId(userObjectId);
        }
        if (authType) {
          addOrUpdateAwaMetaTag(AwaMetaTagType.AuthType, authType);
        }
        if (tenantId) {
          addOrUpdateAwaMetaTag(AwaMetaTagType.TenantId, tenantId);
        }
        if (sessionId) {
          addOrUpdateAwaMetaTag(AwaMetaTagType.SessionId, sessionId);
        }
      } catch {
        console.log('error initializing user telemetry data');
      }
    }
  }

  private addToBodyElement = (namespace: string, theme: string) => {
    document.getElementById('ReactViewBody').className = '';
    document.getElementById('ReactViewBody').classList.add('supCont');
    if (!window.location.href.indexOf(URLs.OfficeLink) && namespace) {
      document.getElementById('ReactViewBody').classList.add(namespace.toLowerCase());
    }
    document.getElementById('ReactViewBody').classList.add(theme);
  };

  private RouteContactUsApp = () => {
    // Route auth consent page and close page for ContactUsApp
    return (
      <Router>
        <Switch>
          <Suspense fallback={null}>
            <Route path={URLs.TeamsAuthStart} component={ConsentPopup} />
            <Route path={URLs.TeamsAuthEnd} component={ClosePopup} />
            <Route path="*" component={ContactUsAppContainer} />
          </Suspense>
        </Switch>
      </ Router>
    );
  };

  // Components to be rendered
  private body = () => {
    this.injectSessionDataTelemetry(this.props.sessionData);
    if (!this.props.hasThirdPartyCookiesEnabled
      // WAC has disabled third party cookie which makes home/userinfo call fails and breaks the whole app
      // Temporarily remove this error message for WAC only, so at least self-help can still work.
      // Need more invetigation on how to enable auth for WAC.
      && this.props.environment !== Environment.agavePane) {
      return <ThirdPartyCookiesDisabledContainer />;
    }
    return (this.props.environment === Environment.agavePane || uwpNamespaces.indexOf(this.props.globalState.namespace) !== -1
      ? (<div>
        <BackgroundAuthContainer />
        <ContactUsAppContainer />
      </div>)
      : this.RouteContactUsApp()
    );
  };
}
